/**
 * FRONT-1113: in-chat approval card for a tier-3 (Propose) tool call.
 *
 * The autonomy gate blocks server-side inside `requestApproval`,
 * polling the approvals service — the tool never returns, so the conversation
 * only ever showed "working" while the actual question sat in the Inspector side
 * panel. A permission question belongs where the user is talking.
 *
 * This card claims the pending approvals whose `originExternalId` matches this
 * chat's session and renders them at the end of the thread. Approving resolves
 * the same approval row the gate is polling, so the blocked tool call simply
 * continues — no turn replay, unlike the tier-4 path.
 */
import { useEffect, useRef, useState } from 'react';
import { getQueryClient } from '~/lib/query-client';
import { PencilSimple } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { ApiError } from '~/lib/api-client';
import { useApprovals, type Approval } from '~/hooks/queries/use-approvals';
import {
  useApprove,
  useReject,
  type ApproveInput,
  type ApproveResponse,
} from '~/hooks/mutations/use-approval-actions';
import {
  draftBlocker,
  draftFromForm,
  draftIsEmpty,
  editFromDraft,
  type ApprovalDraft,
  type ApprovalEdit,
} from './approval-edit-draft';
import { ApprovalEditFormFields } from './approval-edit-form';
import { cardOf } from '~/features/approvals/approval-card-view';
import {
  ApprovalCardBody,
  ApprovalCardButtons,
  ApprovalCardFooter,
  approvalFrameClass,
} from '~/features/approvals/approval-card-body';
import { formatRelativeTime } from '~/lib/relative-time';
import { isLateApproval, scheduleContinuationPickup, shouldPickUpAfterApprove } from './late-continuation-pickup';
import { lateApprovalDeadline, useLateRunOutcomes, type LateOutcome } from './late-approval-outcome';
import { EDITED_SETTLED_COPY, settledApprovedCopy } from './approval-settled-copy';
import { quietSettleDelay } from './approval-settled-copy';

/** The gate's `details` payload — the tool call the user is consenting to. */
type ApprovalDetails = {
  args?: unknown;
  tool?: string;
  domain?: string;
  action?: string;
  planId?: unknown;
  chatAllow?: unknown;
};

function detailsOf(approval: Approval): ApprovalDetails {
  const d = approval.details;
  return d && typeof d === 'object' && !Array.isArray(d) ? (d as ApprovalDetails) : {};
}

/** FRONT-3031: the gate names the live plan a card fell outside of, so the card can say so. */
export function fallsOutsidePlan(approval: Approval): boolean {
  const { planId, domain, action } = detailsOf(approval);
  return typeof planId === 'number' && !(domain === 'system' && action === 'autonomy_change');
}

/**
 * FRONT-2891: the gate offered "Always allow in this chat" on this card. The
 * server decides (never for money, deletes or the person's own Policy) and
 * checks again when it is answered; the card only shows what it was told.
 * Replaces FRONT-3031's "Yes, and don't ask again this plan".
 */
export function offersChatAllow(approval: Approval): boolean {
  const { chatAllow, domain, action } = detailsOf(approval);
  return chatAllow === true && !(domain === 'system' && action === 'autonomy_change');
}

/**
 * FRONT-1311: the card's wording, chosen by what is actually being approved.
 *
 * FRONT-1113 built this card for one caller — a knowledge-graph memory write —
 * and hard-coded that caller's phrasing into every string. The claim filter
 * below is generic, so the card also renders for the other 17 approval-raising
 * autonomy rules (see src/autonomy/defaults.ts): deleting a file, running a
 * sandbox command, spending money, changing eddy's own permissions. Each of
 * those asked "Yes, remember it?" and then reported "Saved to your memory." —
 * describing an action the tool never performed. Consent to the wrong sentence
 * is not consent.
 *
 * Saving to memory keeps the warm phrasing; everything else reads neutrally.
 * The `memory` DOMAIN is not enough to pick it — `graph_remove` and
 * `graph_merge` also live in that domain (action `remove_entity`, both tier 3),
 * and a deletion offered as "Yes, remember it" that settles to "Saved to your
 * memory." contradicts its own description line one row above. Destructive
 * memory actions take the neutral branch with the rest.
 *
 * FRONT-1739 review finding 2: `memory/overwrite_file` — file_write's
 * plain-file mode (src/files/tool.ts) — is ALSO excluded, alongside
 * `remove_entity`. A plain file (`user_files`, no title) is not the user's
 * knowledge graph; "rewrite notes.txt for me" is not "remember this", and a
 * refused write reporting "Saved to your memory." would be the same false
 * claim the deletion case above exists to prevent. `memory/overwrite_document`
 * (a titled Knowledge document) is deliberately left in the warm branch —
 * documents ARE the user's saved memory in this app's own vocabulary
 * (`src/memory/`, the Files/Knowledge surface); only the untitled plain-file
 * pair is wrong here.
 *
 * FRONT-3004: the card's headline and buttons now come from the card the
 * server stores with the request (`cardOf`); what is left here is what the
 * row says once it is answered.
 *
 * Exported for its own unit test: the settled strings are only reachable after a
 * click, so a static-markup render can never assert them — and those are exactly
 * the two that used to claim a save for a delete.
 */
export function copyFor(approval: Approval) {
  const { domain, action } = detailsOf(approval);
  const isSaveToMemory = domain === 'memory' && action !== 'remove_entity' && action !== 'overwrite_file';
  return {
    busy: isSaveToMemory ? 'Saving…' : 'Working…',
    // Not "Done": approving only releases the gate that was holding the call
    // (src/autonomy/gate.ts polls for this). The tool runs afterwards and can
    // still fail, so claiming completion here would be the same premature
    // promise the optimistic-settle comment below argues against.
    approved: isSaveToMemory ? 'Saved to your memory.' : 'Approved — going ahead now.',
    // FRONT-1747: only the memory-save and document-rewrite kinds can be
    // edited, and both are in the warm branch above.
    approvedEdited: EDITED_SETTLED_COPY,
    denied: isSaveToMemory
      ? "Not saved — I'll leave it out."
      : "Skipped — I didn't do it.",
  };
}

/** FRONT-2307: a late run the card could no longer follow. */
export const LOST_TRACK_COPY =
  "This is taking a while and I've lost track of it here — the result will show up in the chat. Tell me to continue once it does.";

/**
 * FRONT-2307 (Dome, PR #1624): what a row that answered "running" settles to
 * once its watcher hears back — the server's own sentence when there is one,
 * so it can never read "Working on it" forever after the run finished.
 * Exported for its own test, like `settledApprovedCopy` (approval-settled-copy.ts).
 */
export function lateOutcomeCopy(o: LateOutcome): { text: string; tone: 'success' | 'destructive' | 'neutral' } {
  if (o.kind === 'lost') return { text: LOST_TRACK_COPY, tone: 'neutral' };
  if (o.kind === 'succeeded') return { text: o.message || 'Done — it finished running.', tone: 'success' };
  return { text: o.message || "That didn't go through — nothing was changed.", tone: 'destructive' };
}

/**
 * Send the user's decision and settle the row on the server's answer.
 *
 * Settles ONLY once the server has accepted it. An optimistic
 * "Saved to your memory." is a lie whenever the call fails (a stale card whose
 * approval row is already gone, say) — it tells the user their memory was
 * updated when nothing was written.
 *
 * Split out of `ApprovalRow` so the component is markup and this is behaviour;
 * the row had grown to mix mutations, settle state and conditional rendering in
 * one place (Aikido, FRONT-1311).
 */
function useApprovalDecision(
  approvalId: string,
  /** FRONT-2307: told the approve response, so a late run's outcome can be shown. */
  onApproved?: (result: ApproveResponse | undefined) => void,
) {
  const approveMut = useApprove();
  const rejectMut = useReject();
  const [decided, setDecided] = useState<'approved' | 'denied' | null>(null);
  const [failed, setFailed] = useState(false);
  // FRONT-1747: the server refused the user's edited version (400) — a
  // sentence written for them, shown in the form so they can fix it. The
  // request is still open; nothing was decided.
  const [editRefusal, setEditRefusal] = useState<string | null>(null);

  const decide = (outcome: 'approved' | 'denied', edit?: ApprovalEdit, allowInChat = false) => {
    setFailed(false);
    setEditRefusal(null);
    const onError = (err: Error) => {
      if (edit && err instanceof ApiError && err.status === 400) setEditRefusal(err.message);
      else setFailed(true);
    };
    if (outcome === 'approved') {
      // FRONT-1747's edited save and a plain Yes share this one success
      // path, so an edited approval on a call a suspended skill is waiting
      // on continues the skill exactly like a plain approve (FRONT-2320).
      let input: ApproveInput = approvalId;
      if (edit) input = { id: approvalId, edit };
      else if (allowInChat) input = { id: approvalId, allowInChat: true };
      approveMut.mutate(input, {
        onSuccess: (data) => {
          setDecided('approved');
          // The tab sends no chat turn after an approve: carrying on (a
          // paused inline skill included) is the server's job (FRONT-3036).
          // FRONT-2307: a late run still going is watched from THIS response so
          // the card can show how it ended (display only, FRONT-3036).
          onApproved?.(data as ApproveResponse | undefined);
        },
        onError,
      });
    } else {
      rejectMut.mutate(approvalId, { onSuccess: () => setDecided('denied'), onError });
    }
  };

  return {
    decided,
    failed,
    editRefusal,
    busy: approveMut.isPending || rejectMut.isPending,
    decide,
    // Only meaningful once `decided === 'approved'` — a reject's response
    // never carries `executed` (see `settledApprovedCopy`'s doc comment).
    approveResult: approveMut.data,
  };
}

/**
 * FRONT-1747: the card in edit mode. Save sends the edit; when every entry of
 * a batch was removed there is nothing left to save, and the primary action
 * becomes the No it now is. The disabled Save and its reason are ONE
 * decision (`draftBlocker`), so the button is never dead without a sentence.
 */
function ApprovalEditPanel({
  draft,
  onChange,
  busy,
  busyLabel,
  refusal,
  failed,
  onSave,
  onSkip,
  onCancel,
}: {
  draft: ApprovalDraft;
  onChange: (next: ApprovalDraft) => void;
  busy: boolean;
  busyLabel: string;
  refusal: string | null;
  failed: boolean;
  onSave: () => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  const blocker = draftBlocker(draft);
  const empty = draftIsEmpty(draft);
  return (
    <div data-testid="approval-edit" className="space-y-2.5">
      <p className="text-[length:var(--text-sm)] font-medium text-foreground">Edit before saving</p>
      <ApprovalEditFormFields draft={draft} onChange={onChange} />
      {blocker && <p className="text-[length:var(--text-sm)] text-destructive">{blocker}</p>}
      {refusal && <p className="text-[length:var(--text-sm)] text-destructive">{refusal}</p>}
      {failed && (
        <p className="text-[length:var(--text-sm)] text-destructive">
          That didn't go through — nothing was changed. This request may have expired; ask EDDY again.
        </p>
      )}
      <div className="flex gap-2 pt-1">
        {empty ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            className="h-8 text-[length:var(--text-sm)] bg-destructive/15 text-destructive hover:bg-destructive/25"
            onClick={onSkip}
          >
            No, skip it
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || blocker !== null}
            className="h-8 text-[length:var(--text-sm)] bg-success/15 text-success hover:bg-success/25"
            onClick={onSave}
          >
            {busy ? busyLabel : 'Save this version'}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} className="h-8 text-[length:var(--text-sm)]" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ApprovalRow({
  approval,
  onDecisionStart,
  onApproved,
  lateOutcome,
  onEditCancel,
  onSettledQuietly,
}: {
  approval: Approval;
  /**
   * FRONT-2653: a settled line that says nothing new has been on screen long
   * enough — release the pin so it stops stacking under the chat.
   */
  onSettledQuietly: (approval: Approval) => void;
  /** FRONT-2307: starts watching a late run so the card can show its outcome. */
  onApproved?: (approvalId: string, result: ApproveResponse | undefined) => void;
  /** FRONT-2307: how a run the card had to wait on (`running`) finally settled. */
  lateOutcome?: LateOutcome;
  /**
   * FRONT-1747: Cancel on the edit form releases the pin that opening it
   * took — the row goes back to following the live list, so a decision made
   * elsewhere afterwards clears it the way it clears any undecided card.
   */
  onEditCancel: (approval: Approval) => void;
  /**
   * FRONT-1639 (adversarial review, F1): called synchronously the instant a
   * decision is made, BEFORE the mutation fires — see `PendingApprovalCards`'
   * doc comment for why the parent needs this to keep the row rendered.
   */
  onDecisionStart: (approval: Approval) => void;
}) {
  const copy = copyFor(approval);
  const card = cardOf(approval);
  const offPlan = fallsOutsidePlan(approval);
  const chatOffer = offersChatAllow(approval);
  const { decided, failed, editRefusal, busy, decide, approveResult } = useApprovalDecision(
    approval.id,
    (result) => onApproved?.(approval.id, result),
  );
  const [draft, setDraft] = useState<ApprovalDraft | null>(null);

  // FRONT-2653: a late run that answered `running` stays while it runs — its
  // outcome arrives later through `lateOutcome`, and only a success fades
  // then (FRONT-3250).
  const quietDelay = quietSettleDelay(decided, approveResult, lateOutcome?.kind);
  // The latest callback, read when the timer fires. Keying the effect on the
  // callback instead would restart the timer on every parent re-render and
  // keep the line up for as long as the chat keeps streaming.
  const settleQuietly = useRef(() => onSettledQuietly(approval));
  settleQuietly.current = () => onSettledQuietly(approval);
  useEffect(() => {
    if (quietDelay === null) return;
    const timer = setTimeout(() => settleQuietly.current(), quietDelay);
    return () => clearTimeout(timer);
  }, [quietDelay]);

  if (decided) {
    const settled =
      decided === 'approved'
        ? approveResult?.executed === 'running' && lateOutcome
          ? lateOutcomeCopy(lateOutcome)
          : settledApprovedCopy(approveResult, copy.approved, copy.approvedEdited)
        : { text: copy.denied, tone: 'neutral' as const };
    return (
      <div
        // FRONT-2653: a line about to go fades over its last 300ms instead of
        // blinking out; reduced motion skips the fade and it simply goes.
        style={quietDelay !== null ? { animationDelay: `${quietDelay - 300}ms` } : undefined}
        className={
          (quietDelay !== null
            ? 'animate-out fade-out fill-mode-forwards duration-300 motion-reduce:animate-none '
            : '') +
          (settled.tone === 'destructive'
            ? 'rounded-xl border border-destructive/30 bg-destructive/[0.07] px-3.5 py-2.5 text-[length:var(--text-base)] text-destructive'
            : 'rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-[length:var(--text-base)] text-muted-foreground')
        }
      >
        {settled.text}
      </div>
    );
  }

  // FRONT-1639: a deferred row may be up to two hours old (FRONT-2307) —
  // this is informed consent for a request the user hasn't seen since the
  // gate stopped polling for it, not a fresh ask. FRONT-2307 adds when it
  // stops being answerable, and that answering picks the task back up.
  const isDeferred = approval.waitingMode === 'deferred';
  const waitingSince = isDeferred ? formatRelativeTime(approval.requestedAt) : null;
  const deadline = isDeferred ? lateApprovalDeadline(approval.answerableUntil) : null;

  return (
    <div data-testid="approval-card" data-kind={card.kind} className={approvalFrameClass(card.kind)}>
      {waitingSince && (
        <p className="mb-1.5 text-[length:var(--text-sm)] text-muted-foreground tabular-nums text-pretty">
          Asked {waitingSince} — still waiting on you.
          {deadline && ` Approve before ${deadline} and I'll pick up where I left off.`}
        </p>
      )}
      {draft ? (
        <ApprovalEditPanel
          draft={draft}
          onChange={setDraft}
          busy={busy}
          busyLabel={copy.busy}
          refusal={editRefusal}
          failed={failed}
          onSave={() => {
            onDecisionStart(approval);
            decide('approved', editFromDraft(draft));
          }}
          onSkip={() => {
            onDecisionStart(approval);
            decide('denied');
          }}
          onCancel={() => {
            setDraft(null);
            onEditCancel(approval);
          }}
        />
      ) : (
        <>
          <ApprovalCardBody approval={approval} card={card} />
          {offPlan && (
            <p className="mt-1.5 text-[length:var(--text-sm)] text-foreground text-pretty">
              This isn&rsquo;t on the plan you approved.
            </p>
          )}
          {failed && (
            <p className="mt-2 text-[length:var(--text-sm)] text-destructive">
              That didn't go through — nothing was changed. This request may have expired; ask EDDY again.
            </p>
          )}
          {/* FRONT-3004: the action first, then the way out, on every screen;
              FRONT-2891's chat offer sits beside the action, Edit last. */}
          <ApprovalCardButtons
            card={card}
            busy={busy}
            busyLabel={copy.busy}
            onConfirm={() => {
              onDecisionStart(approval);
              decide('approved');
            }}
            onCancel={() => {
              onDecisionStart(approval);
              decide('denied');
            }}
          >
            {{
              afterConfirm: chatOffer ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  data-testid="approval-allow-in-chat"
                  className="h-8 text-[length:var(--text-sm)]"
                  onClick={() => {
                    onDecisionStart(approval);
                    decide('approved', undefined, true);
                  }}
                >
                  Always allow in this chat
                </Button>
              ) : undefined,
              afterCancel: approval.editForm ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  className="h-8 text-[length:var(--text-sm)] text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    // Pinned from the moment editing starts, not only on Save:
                    // a decision landing elsewhere (another tab) would otherwise
                    // drop this row from the live list and unmount the form
                    // mid-edit. Pinned, a stale Save reports "didn't go through".
                    onDecisionStart(approval);
                    setDraft(draftFromForm(approval.editForm!, approval.details));
                  }}
                >
                  <PencilSimple size={14} weight="bold" /> Edit
                </Button>
              ) : undefined,
            }}
          </ApprovalCardButtons>
          {chatOffer && (
            <p data-testid="approval-allow-in-chat-note" className="mt-2 text-[length:var(--text-xs)] text-muted-foreground text-pretty">
              &ldquo;Always allow&rdquo; lasts 24 hours, in this chat only. To stop asking everywhere, change it in Policy.
            </p>
          )}
          <ApprovalCardFooter card={card} />
        </>
      )}
    </div>
  );
}

/**
 * The pending approvals raised from THIS chat. `sessionId` is the chat surface's
 * own `web:`/`inbox-` id — the same value the dispatch context forwards as
 * `originExternalId` when the gate creates the approval.
 *
 * FRONT-1311: exported as a hook because the message list needs the COUNT, not
 * just the markup. An approval arriving is neither a new message nor a stream
 * delta, so it fired none of chat-messages.tsx's scroll triggers: the card
 * mounted at the end of the thread, below the fold, and the conversation never
 * followed it down. The card was in the DOM the whole time and the user could
 * only find the request in the Inspector — which is what this ticket reported.
 */
export function usePendingApprovalsForSession(sessionId: string): Approval[] {
  const { data: approvals = [] } = useApprovals();
  return approvals.filter(
    (a) => a.status !== 'approved' && a.status !== 'rejected' && a.originExternalId === sessionId,
  );
}

/**
 * FRONT-1639 (adversarial review, F1): merge the server's live pending list
 * with any approvals THIS component has pinned locally — a decision it
 * started but hasn't rendered the outcome of yet. Pure so it's testable
 * without mounting anything: given the same two inputs it always returns the
 * same rows. FRONT-2653: rows are ordered by when they were asked, oldest
 * first. In the usual run of one command after another, the answered ones
 * sit above the newest question, so what is waiting on the user is the last
 * thing above the composer. Ordering by time rather than by pinned-or-live
 * also means a row never changes place when the server drops it from the
 * live list mid-click, so the buttons under the pointer never swap cards.
 */
export function mergeWithPinned(mine: Approval[], pinned: Record<string, Approval>): Approval[] {
  const seen = new Set(mine.map((a) => a.id));
  const pinnedOnly = Object.values(pinned).filter((a) => !seen.has(a.id));
  const merged = pinnedOnly.length === 0 ? mine : [...mine, ...pinnedOnly];
  // Stable sort: rows without a timestamp keep their place relative to each other.
  return [...merged].sort((a, b) => askedAt(a) - askedAt(b));
}

function askedAt(a: Approval): number {
  const t = a.requestedAt ? Date.parse(a.requestedAt) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * FRONT-1747: drop one pin (Cancel on the edit form). Pure for the same
 * reason as `mergeWithPinned`; returns the same object when nothing changes.
 */
export function releasePinned(pinned: Record<string, Approval>, id: string): Record<string, Approval> {
  if (!pinned[id]) return pinned;
  const { [id]: _released, ...rest } = pinned;
  return rest;
}

/**
 * Renders every pending approval raised from THIS chat.
 *
 * FRONT-1639 (adversarial review, F1): `executeDeferredApproval`
 * (src/autonomy/deferred-execution.ts) broadcasts `approval-decided` right
 * after CLAIMING a deferred row — well before the tool call it just
 * unblocked has actually finished — so every open tab's `useApprovals()`
 * cache (including this one) can drop the row from its list before the
 * click that decided it ever gets a response back. Without the `pinned`
 * state below, that removal deletes this row from `mine`, which unmounts
 * `ApprovalRow` and destroys the very `decided`/`approveResult` state that
 * was about to render the real outcome — including a failure. Once a row
 * starts deciding it is pinned, independent of whatever the shared list
 * says next. FRONT-2653: the pin is released again once a settled line that
 * says nothing new (`quietSettleDelay`) has been on screen briefly, and
 * (FRONT-3250) once a late run's success — already its own chat row — has
 * been readable for a while; a failure, refusal or a run still going stays
 * for the life of the chat.
 */
export function PendingApprovalCards({
  sessionId,
}: {
  sessionId: string;
}) {
  const mine = usePendingApprovalsForSession(sessionId);
  const [pinned, setPinned] = useState<Record<string, Approval>>({});
  // FRONT-3036: ids of LATE (deferred) approvals made from this tab; only
  // these get the history re-reads that pick up the server's continuation.
  const lateIds = useRef<Set<string>>(new Set());
  const pickUp = () => scheduleContinuationPickup(sessionId, { queryClient: getQueryClient() });
  const resumer = useLateRunOutcomes((id, outcome) => {
    if (lateIds.current.has(id) && outcome.kind !== 'lost') pickUp();
  });
  const onApproved = (id: string, result: ApproveResponse | undefined) => {
    resumer.onApproved(id, result);
    const approval = mine.find((a) => a.id === id) ?? pinned[id];
    if (!approval || !isLateApproval(approval)) return;
    lateIds.current.add(id);
    if (shouldPickUpAfterApprove(approval, result)) pickUp();
  };
  const unpinApproval = (approval: Approval) => {
    setPinned((prev) => releasePinned(prev, approval.id));
  };
  const pinApproval = (approval: Approval) => {
    setPinned((prev) => (prev[approval.id] ? prev : { ...prev, [approval.id]: approval }));
  };
  const rendered = mergeWithPinned(mine, pinned);
  if (rendered.length === 0) return null;
  return (
    <div className="space-y-2">
      {rendered.map((a) => (
        <ApprovalRow
          key={a.id}
          approval={a}
          onDecisionStart={pinApproval}
          onEditCancel={unpinApproval}
          onSettledQuietly={unpinApproval}
          onApproved={onApproved}
          lateOutcome={resumer.outcomeOf(a.id)}
        />
      ))}
      {resumer.watchers}
    </div>
  );
}
